import {formatNumber} from '@angular/common';
import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import * as echarts from 'echarts';
import {
  AdminDashboardImpactState,
  AdminDashboardInsightImpact,
  AdminDashboardInsightImpactCompany,
  AdminDashboardTierMix
} from '@features/admin-panel/models/admin-dashboard-impact.models';
import {adbUsdCompact} from '@features/admin-panel/helpers/admin-dashboard-format';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbAlpha, adbCategoryAxis, AdbChartTheme, adbChartTheme, adbRedrawOnThemeChange, adbTooltip} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is read-only,
 * and `spyOn(echarts, 'init')` throws.
 */
export const settingsImpactCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** A change from the saved values to the values on screen: "no change", or "+3" / "−$12.4k" (U+2212), and whether it is bad. */
export interface AdbImpactDelta {
  text: string;
  tone: 'same' | 'bad' | 'good';
}

/** One row of the Impact card's definition list. */
export interface AdbImpactRow {
  key: 'flagged' | 'revenue' | 'renewal' | 'dark' | 'usage' | 'spike';
  label: string;
  /** True for the four per-kind rows, indented under the totals. */
  sub: boolean;
  /** The figure under the values on screen. */
  after: string;
  delta: AdbImpactDelta;
}

/** One tier of the legend under the tier chart. */
export interface AdbImpactTierRow {
  key: keyof AdminDashboardTierMix;
  label: string;
  /** The paying companies in the tier under the values on screen. */
  after: string;
  delta: AdbImpactDelta;
}

/** The four health tiers, in the order the mockup stacks them (charts.ts 643-648). */
export const ADB_IMPACT_TIERS: ReadonlyArray<{key: keyof AdminDashboardTierMix; label: string}> = [
  {key: 'critical', label: 'Critical'},
  {key: 'warning', label: 'Warning'},
  {key: 'watch', label: 'Watch'},
  {key: 'healthy', label: 'Healthy'}
];

/** A count as the dashboard prints it: "1,234". */
export function adbImpactCount(v: number): string {
  return formatNumber(v ?? 0, 'en-US', '1.0-0');
}

/**
 * The change from `before` to `after` (OCA-E-159 settings/page.tsx 157-162): equal → "no change" ('same'); otherwise
 * "+N" / "−N" (U+2212) with `format` applied to the absolute change. `upIsBad` (the default) marks a rise bad and a
 * drop good - more flags or more revenue at risk is worse; pass false where a rise is good (Healthy companies).
 */
export function adbImpactDelta(before: number, after: number, format: (v: number) => string = adbImpactCount, upIsBad = true): AdbImpactDelta {
  const a = before ?? 0;
  const b = after ?? 0;
  if (a === b) {
    return {text: 'no change', tone: 'same'};
  }
  const up = b > a;
  return {text: `${up ? '+' : '−'}${format(Math.abs(b - a))}`, tone: up === upIsBad ? 'bad' : 'good'};
}

/**
 * "Paying companies by health tier" (OCA-E-159 charts.ts 642-674, tierMixOption): two stacked horizontal bars, Saved on
 * top and These values under it; Critical red, Warning amber, Watch blue, Healthy green at 55% alpha; 14 px bars, no
 * animation; an item tooltip "<b>{Tier}</b> · {row}<br/>{n} paying companies".
 */
export function adbTierMixOption(t: AdbChartTheme, before: AdminDashboardTierMix, after: AdminDashboardTierMix): echarts.EChartsOption {
  const rows = ['Saved', 'These values'];
  const colours: Record<keyof AdminDashboardTierMix, string> = {critical: t.red, warning: t.amber, watch: t.blue, healthy: adbAlpha(t.green, 0.55)};
  const option: echarts.EChartsOption = {
    animation: false,
    textStyle: {fontFamily: t.font},
    grid: {left: 4, right: 4, top: 4, bottom: 4, containLabel: true},
    xAxis: {type: 'value', show: false, max: 'dataMax'},
    yAxis: adbCategoryAxis(t, rows, {inverse: true, axisLine: {show: false}}),
    tooltip: adbTooltip(t, {
      trigger: 'item',
      formatter: (p: any) => `<b>${p.seriesName}</b> · ${rows[p.dataIndex]}<br/>${countOf(p.value, 'paying company', 'paying companies')}`
    }),
    series: ADB_IMPACT_TIERS.map(tier => ({
      name: tier.label,
      type: 'bar' as const,
      stack: 'mix',
      barWidth: 14,
      data: [before?.[tier.key] ?? 0, after?.[tier.key] ?? 0],
      itemStyle: {color: colours[tier.key]}
    }))
  };
  return option;
}

/**
 * The Impact card's subtitle per state (OCA-E-159 settings/page.tsx 335-342). 'edited' names the companies compared:
 * the settings page previews every client company with no filters, because the dashboard does not keep its scope or
 * filters once you leave it (ITP-E-168-06).
 */
export const ADB_IMPACT_SUBTITLES: Readonly<Record<AdminDashboardImpactState, string>> = Object.freeze({
  edited: 'Saved values against the values on screen, over every client company.',
  unedited: 'No unsaved changes. Edit a value to see what it does.',
  paused: 'Paused on the last valid values until every field is valid and the weights add up to 100%.',
  saved: 'Showing the saved values until every field is valid and the weights add up to 100%.'
});

/**
 * The prediction settings page's Impact card (ITP-E-168-05; OCA-E-159 settings/page.tsx 334-402): what the saved
 * thresholds and weights flag against what the values on screen would flag, from AdminDashboardInsightImpactQuery.
 * Presentational: the settings page loads the preview and passes it in.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-settings-impact',
  templateUrl: './admin-dashboard-settings-impact.component.html',
  styleUrls: ['./admin-dashboard-settings-impact.component.scss']
})
export class AdminDashboardSettingsImpactComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  /** The latest preview; null until the first one arrives. */
  @Input() impact: AdminDashboardInsightImpact | null = null;
  @Input() state: AdminDashboardImpactState = 'unedited';
  /** True while a preview is on its way. */
  @Input() loading = false;
  /** True when the latest preview failed; the last good one stays on screen. */
  @Input() error = false;
  @ViewChild('chart', {static: true}) chartContainer: ElementRef<HTMLDivElement>;

  /** Flagged companies, Revenue at risk, then the four kinds; empty before the first nightly run. Built once per preview. */
  rows: AdbImpactRow[] = [];
  /** The legend under the tier chart: each tier's count under these values and its change. Built once per preview. */
  tierRows: AdbImpactTierRow[] = [];

  private chart: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;

  get subtitle(): string {
    return ADB_IMPACT_SUBTITLES[this.state] ?? ADB_IMPACT_SUBTITLES.unedited;
  }

  /** Flagged companies, Revenue at risk, then the four kinds (renewal, dark, usage, spike); empty before the first nightly run. */
  private buildRows(): AdbImpactRow[] {
    const before = this.impact?.Before;
    const after = this.impact?.After;
    if (!this.impact?.AsOf || !before || !after) {
      return [];
    }
    return [
      {key: 'flagged', label: 'Flagged companies', sub: false, after: adbImpactCount(after.flagged), delta: adbImpactDelta(before.flagged, after.flagged)},
      {key: 'revenue', label: 'Revenue at risk', sub: false, after: adbUsdCompact(after.revenueAtRisk),
        delta: adbImpactDelta(before.revenueAtRisk, after.revenueAtRisk, adbUsdCompact)},
      {key: 'renewal', label: 'Renewal risk', sub: true, after: adbImpactCount(after.renewal), delta: adbImpactDelta(before.renewal, after.renewal)},
      {key: 'dark', label: 'Going dark', sub: true, after: adbImpactCount(after.dark), delta: adbImpactDelta(before.dark, after.dark)},
      // ITP-E-216-08. Usage falling; an older server sends no `usage`, read as 0.
      {key: 'usage', label: 'Usage falling', sub: true, after: adbImpactCount(after.usage ?? 0), delta: adbImpactDelta(before.usage ?? 0, after.usage ?? 0)},
      {key: 'spike', label: 'Spend spikes', sub: true, after: adbImpactCount(after.spike), delta: adbImpactDelta(before.spike, after.spike)}
    ];
  }

  /** The legend under the tier chart: each tier's count under these values and its change (a rise in Healthy is good). */
  private buildTierRows(): AdbImpactTierRow[] {
    const before = this.impact?.Before?.tiers;
    const after = this.impact?.After?.tiers;
    if (!this.impact?.AsOf || !before || !after) {
      return [];
    }
    return ADB_IMPACT_TIERS.map(tier => ({
      key: tier.key,
      label: tier.label,
      after: adbImpactCount(after[tier.key]),
      delta: adbImpactDelta(before[tier.key], after[tier.key], adbImpactCount, tier.key !== 'healthy')
    }));
  }

  /** "Newly flagged · 3": the companies the values on screen flag and the saved ones do not (AddedTotal, not just the rows sent). */
  get addedTitle(): string {
    return `Newly flagged · ${adbImpactCount(this.impact?.AddedTotal ?? 0)}`;
  }

  /** "No longer flagged · 2": the companies the saved values flag and the values on screen do not (RemovedTotal). */
  get removedTitle(): string {
    return `No longer flagged · ${adbImpactCount(this.impact?.RemovedTotal ?? 0)}`;
  }

  /** At most 6 rows of a list (OCA-E-159 settings/page.tsx 378, 392; the server already sends no more than 6). */
  firstSix(companies: AdminDashboardInsightImpactCompany[] | null | undefined): AdminDashboardInsightImpactCompany[] {
    return (companies ?? []).slice(0, 6);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['impact']) {
      // ITP-E-168-09. Built here, not in getters: a getter hands ngFor new objects on every change-detection pass, and
      // re-rendering the legend each pass loops forever under the app's in-zone MutationObserver.
      this.rows = this.buildRows();
      this.tierRows = this.buildTierRows();
      this.render();
    }
  }

  ngAfterViewInit(): void {
    this.chart = settingsImpactCharts.init(this.chartContainer.nativeElement);
    this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    this.render();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
    this.chart = null;
  }

  trackRow(index: number, row: {key: string}): string {
    return row.key;
  }

  /** Draws the tier chart for the current preview; nothing before the first nightly run. */
  private render(): void {
    const before = this.impact?.Before?.tiers;
    const after = this.impact?.After?.tiers;
    if (!this.chart || !this.impact?.AsOf || !before || !after) {
      return;
    }
    this.chart.setOption(adbTierMixOption(adbChartTheme(this.chartContainer.nativeElement), before, after), true);
  }
}
